/**
 * Admin « Tarifs et promos » (migration 0717) : réglages des pass et de
 * l'essai gratuit (`offer_settings`), promos SKILLCARDS
 * (`skillcard_promotions`). Écritures directes sous RLS admin ; tables
 * postérieures aux types générés → client générique.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export interface AdminOfferSettings {
  pass_24h_enabled: boolean;
  pass_7d_enabled: boolean;
  trial_enabled: boolean;
  trial_days: number;
  updated_at: string;
}

export interface SkillcardPromotion {
  id: string;
  label: string;
  percent: number;
  starts_at: string;
  ends_at: string;
  created_at: string;
}

export type PromoStatus = 'active' | 'upcoming' | 'ended';

/** Promo en cours, à venir ou terminée. */
export function promoStatus(
  p: Pick<SkillcardPromotion, 'starts_at' | 'ends_at'>,
  now = new Date(),
): PromoStatus {
  const t = now.getTime();
  if (Date.parse(p.ends_at) <= t) return 'ended';
  if (Date.parse(p.starts_at) > t) return 'upcoming';
  return 'active';
}

/** En cours d'abord, puis à venir (la plus proche d'abord), puis terminées (récentes d'abord). */
export function sortPromotions<T extends Pick<SkillcardPromotion, 'starts_at' | 'ends_at'>>(
  list: readonly T[],
  now = new Date(),
): T[] {
  const rank: Record<PromoStatus, number> = { active: 0, upcoming: 1, ended: 2 };
  return [...list].sort((a, b) => {
    const sa = promoStatus(a, now);
    const sb = promoStatus(b, now);
    if (sa !== sb) return rank[sa] - rank[sb];
    if (sa === 'ended') return Date.parse(b.ends_at) - Date.parse(a.ends_at);
    return Date.parse(a.starts_at) - Date.parse(b.starts_at);
  });
}

/** Valeur d'un `<input type="datetime-local">` (heure de l'appareil). */
export function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Instant ISO d'une valeur `datetime-local`, ou null si elle est illisible. */
export function fromLocalInput(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;
  const d = new Date(value);
  return Number.isFinite(d.getTime()) ? d.toISOString() : null;
}

export interface PromoInput {
  label: string;
  percent: string;
  starts: string;
  ends: string;
}

/** Promo à enregistrer, ou le message d'erreur à afficher. */
export function parsePromoInput(
  input: PromoInput,
):
  | { ok: true; value: Omit<SkillcardPromotion, 'id' | 'created_at'> }
  | { ok: false; error: string } {
  const percent = Number(input.percent.replace(',', '.'));
  if (!Number.isInteger(percent) || percent < 5 || percent > 90) {
    return { ok: false, error: 'Réduction entre 5 et 90 %, sans virgule.' };
  }
  const label = input.label.trim();
  if (label.length > 60) return { ok: false, error: 'Nom de 60 caractères au plus.' };
  const starts = fromLocalInput(input.starts);
  const ends = fromLocalInput(input.ends);
  if (!starts || !ends) return { ok: false, error: 'Indique le début et la fin.' };
  if (Date.parse(ends) <= Date.parse(starts)) {
    return { ok: false, error: 'La fin doit venir après le début.' };
  }
  return { ok: true, value: { label, percent, starts_at: starts, ends_at: ends } };
}

function invalidate(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ['admin-offer-settings'] });
  void qc.invalidateQueries({ queryKey: ['skillcard-promotions'] });
  void qc.invalidateQueries({ queryKey: ['offer-settings'] });
  void qc.invalidateQueries({ queryKey: ['skillcard-price-grid'] });
}

export function useAdminOfferSettings() {
  return useQuery({
    queryKey: ['admin-offer-settings'],
    queryFn: async (): Promise<AdminOfferSettings> => {
      const { data, error } = await db
        .from('offer_settings')
        .select('pass_24h_enabled, pass_7d_enabled, trial_enabled, trial_days, updated_at')
        .eq('id', true)
        .single();
      if (error) throw new Error(error.message);
      return data as AdminOfferSettings;
    },
  });
}

export function useSaveOfferSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<Omit<AdminOfferSettings, 'updated_at'>>) => {
      const { error } = await db.from('offer_settings').update(patch).eq('id', true);
      if (error) throw new Error(error.message);
    },
    onSettled: () => invalidate(qc),
  });
}

export function useSkillcardPromotions() {
  return useQuery({
    queryKey: ['skillcard-promotions'],
    queryFn: async (): Promise<SkillcardPromotion[]> => {
      const { data, error } = await db
        .from('skillcard_promotions')
        .select('id, label, percent, starts_at, ends_at, created_at')
        .order('starts_at', { ascending: false })
        .limit(100);
      if (error) throw new Error(error.message);
      return (data ?? []) as SkillcardPromotion[];
    },
  });
}

export function useSavePromotion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { id?: string } & Omit<SkillcardPromotion, 'id' | 'created_at'>) => {
      const row = {
        label: p.label,
        percent: p.percent,
        starts_at: p.starts_at,
        ends_at: p.ends_at,
      };
      const { error } = p.id
        ? await db.from('skillcard_promotions').update(row).eq('id', p.id)
        : await db.from('skillcard_promotions').insert(row);
      if (error) throw new Error(error.message);
    },
    onSettled: () => invalidate(qc),
  });
}

export function useDeletePromotion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await db.from('skillcard_promotions').delete().eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSettled: () => invalidate(qc),
  });
}
